import React, { useEffect, useState } from 'react';
import SharedScript from '@americanexpress/shared-scripts';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { IntlProvider } from 'react-intl';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { ProgressCircular } from '@americanexpress/dls-react';
import childRoutes from '../childRoutes';
import PageWrapper from './PageWrapper';
import reducer from '../ducks';
import { MODULE_NAME } from '../utils/constants';
import '../styles/Theme.scss';
import useAuthentication from '../hooks/useAuthentication';

const DpTravelWishlistRoot = ({ languageData, locale }) => {
  const isAuthenticated = useAuthentication(); // comment for local
  // const isAuthenticated = true; // uncomment for local
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const applyTheme = () => {
      const initialTheme = localStorage.getItem('theme') || 'light';
      setTheme(initialTheme);
    };

    applyTheme();

    const handleStorageChange = (e) => {
      if (e.key === 'theme') {
        applyTheme();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  if (Object.entries(languageData).length === 0) {
    return <ProgressCircular />;
  }

  return (
    <IntlProvider locale={locale} messages={languageData}>
      <SharedScript
        name="one-identity-session"
        version="^1.0.0"
        windowCheck="AmexSession"
      />
      <Helmet
        htmlAttributes={{ lang: locale }}
        title="Wishlist AI"
        link={[
          {
            rel: 'stylesheet',
            href: 'https://www.aexp-static.com/cdaas/dls/packages/@americanexpress/dls/7.9.0/stylesheets/dls-core.min.css',
          },
          {
            rel: 'icon',
            href: 'https://www.americanexpress.com/favicon.ico',
          },
        ]}
        meta={[
          { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ]}
      />
      <main
        data-theme={theme}
        style={{ height: '100vh', width: '100vw' }}
      >
        {isAuthenticated ? (
          <PageWrapper />
        ) : (
          <div className="flex height-full flex-align-center flex-justify-center">
            <h1 className="sr-only">Test</h1>
            <ProgressCircular />
          </div>
        )}
      </main>
    </IntlProvider>
  );
};

export const TestableDpTravelWishlistRoot = DpTravelWishlistRoot;

DpTravelWishlistRoot.propTypes = {
  languageData: PropTypes.shape({}).isRequired, // no need to restate all the keys in the lang pack
  locale: PropTypes.string.isRequired,
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
DpTravelWishlistRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  DpTravelWishlistRoot.appConfig = require('../appConfig').default;
}

export const loadModuleData = ({ store: { dispatch } }) => dispatch(
  loadLanguagePack(MODULE_NAME, { fallbackLocale: 'en-US' })
);

DpTravelWishlistRoot.holocron = {
  name: MODULE_NAME,
  reducer,
  loadModuleData,
};

export default oneAppModuleWrapper(MODULE_NAME)(
  DpTravelWishlistRoot
);
